import { useState } from "react";
import { ACCOUNT_ROLE_ELIGIBLE_TYPES } from "@common/constants/domains/accounting/accountRole";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { isAccountType } from "@common/constants/domains/accounting/helpers";
import { AccountRole } from "@common/types/domains/accounting/account";
import { EntityProfileType } from "@common/types/entity";
import { Button } from "@design-system/Button/Button";
import { Field } from "@design-system/Field/Field";
import {
    Modal,
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { Picker } from "@design-system/Picker/Picker.lib";
import { VStack } from "@design-system/VStack/VStack";
import { AccountCodeNameInput } from "@components/accounting/chartOfAccounts/AccountCodeNameInput";
import { buildAccountTypeTree } from "@components/forms/AccountTypeSelectField/buildAccountTypeTree";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import {
    CreateRoleAccountValues,
    getDefaultRoleAccount,
} from "./requiredAccountsMapping";

interface Props {
    role: AccountRole;
    initialName?: string;
    entityLegalType: EntityProfileType;
    suggestCodeText: (type: AccountType) => string | null;
    isCodeTextTaken: (codeText: string) => boolean;
    onClose: () => void;
    onCreate: (values: CreateRoleAccountValues) => void;
}

export const CreateRoleAccountModal = ({
    role,
    initialName,
    entityLegalType,
    suggestCodeText,
    isCodeTextTaken,
    onClose,
    onCreate,
}: Props) => {
    const showAccountNumbers = useShowAccountNumbers();
    const eligibleTypes = ACCOUNT_ROLE_ELIGIBLE_TYPES[role];
    const defaultAccount = getDefaultRoleAccount({ role, entityLegalType });
    const [name, setName] = useState(initialName ?? defaultAccount.name);
    const [type, setType] = useState<AccountType>(defaultAccount.type);
    const [editedCodeText, setEditedCodeText] = useState<string | null>(null);
    const codeText = editedCodeText ?? suggestCodeText(type) ?? "";
    const trimmedName = name.trim();
    const trimmedCodeText = codeText.trim();
    const isCodeTaken =
        trimmedCodeText !== "" && isCodeTextTaken(trimmedCodeText);

    return (
        <Modal show onClose={onClose}>
            <StandardModalHeader>Create new account</StandardModalHeader>
            <StandardModalBody>
                <VStack gap={16}>
                    <AccountCodeNameInput
                        size="lg"
                        codeText={codeText}
                        name={name}
                        isCodeHidden={!showAccountNumbers}
                        codeError={
                            isCodeTaken ? "Code already in use" : undefined
                        }
                        onCodeTextChange={setEditedCodeText}
                        onNameChange={setName}
                    />
                    <Field label="Account type" control="button">
                        <Picker.Select
                            items={buildAccountTypeTree(eligibleTypes)}
                            value={type}
                            onValueChange={(value) => {
                                if (value != null && isAccountType(value)) {
                                    setType(value);
                                }
                            }}
                            defaultExpandAll
                            trigger={
                                <Picker.Button
                                    variant="primary"
                                    size="lg"
                                    block
                                />
                            }
                        />
                    </Field>
                </VStack>
            </StandardModalBody>
            <StandardModalFooter>
                <Button variant="secondary" size="xl" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    size="xl"
                    disabled={trimmedName === "" || isCodeTaken}
                    onClick={() =>
                        onCreate({
                            role,
                            name: trimmedName,
                            type,
                            codeText:
                                trimmedCodeText === "" ? null : trimmedCodeText,
                        })
                    }
                >
                    Create
                </Button>
            </StandardModalFooter>
        </Modal>
    );
};
